import React, { useState, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Bell, BellRinging } from "@phosphor-icons/react";
import { http, fmtDate } from "@/lib/api";

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: items = [] } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => http.get("/notifications").then((r) => r.data),
    refetchInterval: 30000,
  });

  const unread = items.filter((n) => !n.read).length;

  useEffect(() => {
    const onDocClick = (e) => {
      if (!ref.current || ref.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const markRead = async (id) => {
    try {
      await http.post(`/notifications/${id}/read`);
      qc.invalidateQueries({ queryKey: ["notifications"] });
    } catch (err) { console.warn("[notifications] mark-read failed", err); }
  };

  const markAllRead = async () => {
    try {
      await http.post("/notifications/read-all");
      qc.invalidateQueries({ queryKey: ["notifications"] });
    } catch (err) { console.warn("[notifications] mark-all-read failed", err); }
  };

  const openItem = (n) => {
    if (!n.read) markRead(n.id);
    if (n.job_id) {
      navigate(`/jobs/${n.job_id}`);
      setOpen(false);
    }
  };

  return (
    <div ref={ref} className="relative">
      <button
        data-testid="notif-bell-button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Notifications"
        className="relative h-9 w-9 inline-flex items-center justify-center border border-slate-200 rounded-md hover:bg-slate-50 transition-colors"
      >
        {unread > 0 ? <BellRinging size={16} weight="fill" color="#002FA7" /> : <Bell size={16} />}
        {unread > 0 && (
          <span
            data-testid="notif-bell-badge"
            className="absolute -top-1 -right-1 h-4 min-w-[16px] px-1 rounded-full bg-rose-600 text-white text-[10px] font-mono-data flex items-center justify-center leading-none"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notif-panel"
          className="absolute right-0 top-11 w-[360px] bg-white border border-slate-200 rounded-md shadow-lg z-50"
        >
          <div className="flex items-center justify-between px-3 py-2.5 border-b border-slate-200">
            <div className="overline">Notifications {unread > 0 && <span className="ml-1 text-rose-600">· {unread} new</span>}</div>
            {unread > 0 && (
              <button
                data-testid="notif-mark-all-read"
                onClick={markAllRead}
                className="text-[11px] text-[#002FA7] hover:underline"
              >
                Mark all read
              </button>
            )}
          </div>
          <div className="max-h-[420px] overflow-y-auto divide-y divide-slate-100">
            {items.length === 0 ? (
              <div data-testid="notif-empty" className="p-8 text-center text-sm text-slate-400">
                You're all caught up.
              </div>
            ) : (
              items.slice(0, 50).map((n) => (
                <button
                  key={n.id}
                  data-testid={`notif-item-${n.id}`}
                  onClick={() => openItem(n)}
                  className={`w-full text-left px-3 py-2.5 hover:bg-slate-50 transition-colors flex gap-2 ${
                    !n.read ? "bg-blue-50/40" : ""
                  }`}
                >
                  <span
                    className={`h-2 w-2 mt-1.5 rounded-full shrink-0 ${
                      !n.read ? "bg-[#002FA7]" : "bg-slate-300"
                    }`}
                  />
                  <div className="min-w-0 flex-1">
                    <div
                      className={`text-sm truncate ${
                        !n.read ? "font-medium text-slate-900" : "text-slate-700"
                      }`}
                    >
                      {n.title}
                    </div>
                    <div className="text-xs text-slate-500 line-clamp-2 mt-0.5">{n.message}</div>
                    <div className="text-[10px] text-slate-400 font-mono-data mt-1">
                      {fmtDate(n.created_at)}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
